import { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import * as apiRecipeImageSearch from 'api/aiFunctions/PostSimilarRecipesByImageV1';
import MenuPlan from 'classes/MenuPlan/Detail/MenuPlan';
import Recipe from 'classes/Recipe/Search/Recipe';
import RecipeList from 'classes/Recipe/Search/RecipeList';
import Search from 'classes/Recipe/Search/Search';
import RecipeAdvancedSearch from 'components/desktop/Recipe/AdvancedSearch/AdvancedSearch';
import SearchResultList from 'components/desktop/_general/AddMenuOrRecipe/RecipesList/SearchResultList';
import SearchResultThumbnail, {
	ThumbnailCard,
} from 'components/desktop/_general/AddMenuOrRecipe/RecipesList/SearchResultThumbnail/SearchResultThumbnail';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import DropdownPageSize from 'components/desktop/_general/DropdownPageSize/DropdownPageSize';
import DropdownSortOrder from 'components/desktop/_general/DropdownSortOrder/DropdownSortOrder';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import NavigationPillsButtons from 'components/desktop/_general/NavigationPills/NavigationPillsButtons';
import NavigationPillsContent from 'components/desktop/_general/NavigationPills/NavigationPillsContent';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import {
	AirListRowDiscriminatorName,
	IAirListRow,
} from 'components/desktop/_general/SearchResultList/IAirListRow';
import { numberOfRecommendations } from 'constants/imageSearchNumberOfRecommendations';
import { RecipeSortOrder } from 'enums/SortOrder/RecipeSortOrder';
import clone from 'functions/clone';
import { getPicturePath } from 'functions/getPicturePath';
import getSeasonalityCode from 'functions/getSeasonalityCode';
import usePreferredAllergens from 'hooks/redux/usePreferredAllergens';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { ISelectItem } from 'interfaces/ISelectItem';
import { useMenuHandlers } from 'reducers/MenuReducer';
import { RootState } from 'reducers/rootReducer';
import { MenuEditType } from 'types/Menu/Menu';

interface IProps {
	SourceOfAddingType: 'menu' | 'menuPlan' | 'menuCopy';
	SourceOfAddingId: string | null;
	SourceOfAddingRow: number;
	SourceOfAddingCol: number;
	finalizeAdding: () => void;
}

const maxNumberOfButtons = 10;

const RecipeSearchComponent = (props: IProps) => {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [searchIsLoading, setSearchIsLoading] = useState(false);
	const [recipeList, setRecipeList] = useState<RecipeList>(new RecipeList());
	const [search, setSearch] = useState<Search>(new Search());
	const [renderRanges, setRenderRanges] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');
	const preferredAllergens = usePreferredAllergens();
	const navigate = useNavigate();
	const reduxCurrency: string = useSelector((state: RootState) => state.currency);
	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);
	const reduxPreferredNutrientIds = useSelector((state: RootState) => state.preferredNutrientIds);
	const reduxValueScope = useSelector((state: RootState) => state.valueScope);
	const resourceType = props.SourceOfAddingType;

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode, preferredAllergens]);

	const initialize = async (): Promise<void> => {
		setSearchIsLoading(true);
		await getRecipes();
		await search.initialize();
		setSearch(clone(search));
		setSearchIsLoading(false);
	};

	const handleChangeUrl = () => {
		if (search.recipeIds !== null) {
			getRecipes();
		} else {
			setRecipeList(new RecipeList());
			setIsLoading(false);
		}
	};

	const hadleChangeAdvancedSearchUrl = () => {
		search.pageIndex.value = 0;
		handleChangeUrl();
	};

	const getRecipes = async (): Promise<void> => {
		setIsLoading(true);
		const response = await recipeList.callApi(
			search.mapToRequest(
				reduxCurrency,
				reduxValueScope,
				reduxPreferredNutrientIds,
				preferredAllergens
			)
		);
		response.do((x) => {
			recipeList.mapFromApi(x);
			setRecipeList(clone(recipeList));
			search.mapFromApi(x);
		});
		setIsLoading(false);
	};

	const handleReset = () => {
		search.reset();
		setSearchTerm('');
		handleChangeUrl();
	};

	const handleSearch = (input?: string) => {
		if (search.recipeIds === null) {
			search.recipeIds = [];
		}
		search.setSearchTerm(input ?? searchTerm);
		handleChangeUrl();
	};

	const handleChangeSortOrder = (value: string) => {
		search.sortOrder.value = value as RecipeSortOrder;
		handleChangeUrl();
	};

	const handleChangePageSize = (value: string): void => {
		search.pageSize.value = Number(value);
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const getSuggestedRecipes = async (): Promise<ISelectItem[]> => {
		if (searchTerm.length >= 1) {
			await recipeList.search(searchTerm, false);
		} else {
			recipeList.filtered = [];
		}
		setRecipeList(clone(recipeList));
		return recipeList.filtered;
	};

	const advancedSearchContainer = document.getElementById('advancedSearch');
	if (advancedSearchContainer) {
		advancedSearchContainer.addEventListener('shown.bs.collapse', function () {
			setRenderRanges(true);
		});
		advancedSearchContainer.addEventListener('hidden.bs.collapse', function () {
			setRenderRanges(false);
		});
	}

	const isDefaultView = (descriptor: string): boolean => {
		const defaultView: string | null = localStorage.getItem('recipe-search-view');
		if (descriptor === defaultView) {
			return true;
		}
		if (defaultView === null) {
			if (descriptor === 'list') {
				return true;
			}
		}
		return false;
	};

	const getSearchResultListData = (): IAirListRow[] => {
		return recipeList.all.map((recipe: Recipe) => {
			return {
				allergens: recipe.allergenList.all,
				archive: false,
				author: recipe.lastEditor,
				buttons: renderButtons(recipe),
				calories: recipe.calories,
				carbonDioxideColor: recipe.carbonDioxideLabel?.color ?? '',
				carbonDioxideLabel: recipe.carbonDioxideLabel?.label ?? '',
				currency: reduxCurrency,
				dateLastEdited: recipe.lastEditedDateUtc,
				discriminator: AirListRowDiscriminatorName,
				hasNutriScore: recipe.hasNutriScore,
				href: `/recipe/detail/${recipe.id}`,
				id: recipe.id,
				imageUrl: getPicturePath(recipe.image, 348.5),
				nutriScore: recipe.nutriScore,
				prepareTime: recipe.prepareTime,
				price: recipe.price,
				seasonalityCode: getSeasonalityCode(recipe.seasonList.all),
				status: recipe.status?.name || '',
				statusColor: recipe.status?.color || '',
				tags: recipe.tagList.all,
				title: recipe.title,
				valueScope: reduxValueScope,
			};
		});
	};

	const handleAddToMenuOrMenuPlan = async (id: string): Promise<void> => {
		if (resourceType === 'menu' || resourceType === 'menuCopy') {
			await handleAddToMenu(id);
		} else if (props.SourceOfAddingId && resourceType === 'menuPlan') {
			await handleAddToMenuPlan(id);
		}
	};
	const handler = useMenuHandlers();

	const handleAddToMenu = async (id: string) => {
		setIsLoading(true);
		if (resourceType === 'menu' || resourceType === 'menuCopy') {
			const recipe = recipeList.all.filter((x) => x.id === id).first();

			if (props.SourceOfAddingId && resourceType === 'menu') {
				await handler.handleAddRecipe({
					currencyCode: reduxCurrency,
					recipeId: id,
					prepareTime: recipe.prepareTime,
					type: MenuEditType.EDIT,
					menuId: props.SourceOfAddingId,
				});
				props.finalizeAdding();
				navigate(`/menu/detail/${props.SourceOfAddingId}?edit=true`);
			} else if (props.SourceOfAddingId && resourceType === 'menuCopy') {
				await handler.handleAddRecipe({
					currencyCode: reduxCurrency,
					recipeId: id,
					prepareTime: recipe.prepareTime,
					type: MenuEditType.COPY,
					menuId: props.SourceOfAddingId,
				});
				props.finalizeAdding();
				navigate(`/menu/copy/${props.SourceOfAddingId}?edit=true`);
			} else {
				await handler.handleAddRecipe({
					currencyCode: reduxCurrency,
					recipeId: id,
					prepareTime: recipe.prepareTime,
					type: MenuEditType.NEW,
					menuId: null,
				});
				props.finalizeAdding();
				navigate('/menu/new?edit=true');
			}
		}
		setIsLoading(false);
	};

	const handleAddToMenuPlan = async (id: string): Promise<void> => {
		setIsLoading(true);
		const menuPlanId: string | null = props.SourceOfAddingId;
		const rowIndex: string | undefined = props.SourceOfAddingRow.toString();
		const day: string | undefined = props.SourceOfAddingCol.toString();
		if (menuPlanId && rowIndex && day) {
			const menuPlan = new MenuPlan();
			await menuPlan.callApiGet(menuPlanId, reduxCurrency);
			menuPlan.setRecipeToRow(Number(day), Number(rowIndex), id);
			await menuPlan.saveToApi(reduxCurrency);
		}
		props.finalizeAdding();
		setIsLoading(false);
	};

	const handleImageSearch = async (input: FormData | null) => {
		if (input) {
			setIsLoading(true);
			const response = await apiRecipeImageSearch.callApi(input, numberOfRecommendations);
			if (response.hasValue()) {
				const result = response.get();
				if (result.isEmpty()) {
					search.recipeIds = null;
				} else {
					search.recipeIds = result;
				}

				search.sortOrder.value = RecipeSortOrder.RecipeFilterOrder;
				handleChangeUrl();
			}
		}
	};

	const getButtonDataAdd = (id: string): BtnData | undefined => {
		if (resourceType == 'menu' || resourceType == 'menuCopy' || resourceType == 'menuPlan') {
			return {
				onClick: () => handleAddToMenuOrMenuPlan(id),
			};
		}
	};

	const renderButtons = (recipe: Recipe): JSX.Element => {
		return <BtnGroup add={getButtonDataAdd(recipe.id)} />;
	};

	const getSearchResultThumbnailData = (): ThumbnailCard[] => {
		return recipeList.all.map((recipe: Recipe) => {
			return {
				archive: false,
				author: recipe.lastEditor,
				buttons: renderButtons(recipe),
				calories: recipe.calories,
				currency: reduxCurrency,
				dateLastEdited: recipe.lastEditedDateUtc,
				href: `/recipe/detail/${recipe.id}`,
				id: recipe.id,
				imageUrl: getPicturePath(recipe.image, 348.5),
				prepareTime: recipe.prepareTime,
				price: recipe.price,
				status: recipe.status?.name || '',
				statusColor: recipe.status?.color || '',
				title: recipe.title,
				valueScope: reduxValueScope,
			};
		});
	};

	const renderTitle = (): JSX.Element => {
		return <h3>{t('menu:ADD_RECIPE')}</h3>;
	};

	return (
		<main className="container" style={{ height: '70vh', overflowY: 'auto' }}>
			<LoadingAnimation isLoading={isLoading} isModal={true} />
			<div className="d-flex justify-content-between">{renderTitle()}</div>
			<br />
			<div className="row">
				<div className="col-lg-6">
					<div className="input-group mb-3">
						<AdvancedSearchInput
							debounceMilliSeconds={350}
							getSuggestedItems={getSuggestedRecipes}
							handleImageSearch={handleImageSearch}
							handleSearch={handleSearch}
							searchTerm={searchTerm}
							setSearchTerm={setSearchTerm}
							triggerSearchLetterAmount={1}
						/>
					</div>
				</div>
				<div className="col-lg-3">
					<button
						id="advancedSearchButton"
						className="btn btn-primary"
						type="button"
						onClick={() => setRenderRanges(true)}
						data-bs-toggle="collapse"
						data-bs-target="#advancedSearch"
						aria-expanded="true"
						aria-controls="advancedSearch"
						disabled={searchIsLoading}
					>
						{searchIsLoading ? <Spinner animation="border" size="sm" /> : ''}
						{t('_general:ADVANCED_FILTER')}
					</button>
				</div>
				<div className="col-lg-3 text-end">
					<div className="d-flex flex-row justify-content-end">
						<DropdownPageSize
							className="me-2"
							listFunction={handleChangePageSize}
							currentValue={String(search.getPageSize())}
						/>
						<DropdownSortOrder
							type={'recipe'}
							className="me-3"
							listFunction={handleChangeSortOrder}
							currentValue={search.getSortOrder()}
							showRelevance={Boolean(search.recipeIds && search.recipeIds.length)}
						/>
						<NavigationPillsButtons
							className="justify-content-end"
							button1={{
								icon: <i className="bi bi-border-all"></i>,
								descriptor: 'list',
								default: isDefaultView('list'),
							}}
							button2={{
								icon: <i className="bi bi-list-task"></i>,
								descriptor: 'thumbnail',
								default: isDefaultView('thumbnail'),
							}}
							localStorageItemName="recipe-search-view"
						/>
					</div>
				</div>
			</div>
			<div className={'collapse'} id="advancedSearch">
				<div className="card card-body mb-3">
					<RecipeAdvancedSearch
						disabled={recipeList.totalCount === 0}
						search={search}
						setSearch={setSearch}
						handleChangeUrl={hadleChangeAdvancedSearchUrl}
						handleReset={handleReset}
						renderRanges={renderRanges}
					/>
				</div>
			</div>
			<p>{`${recipeList.totalCount} ${t('_general:RECIPES')}`}</p>

			<NavigationPillsContent
				isLoading={false}
				content1={{
					content: <SearchResultList data={getSearchResultListData()} />,
					descriptor: 'thumbnail',
					default: isDefaultView('thumbnail'),
				}}
				content2={{
					content: <SearchResultThumbnail data={getSearchResultThumbnailData()} />,
					descriptor: 'list',
					default: isDefaultView('list'),
				}}
			/>
			<Pagination
				countOfAllListItems={recipeList.totalCount}
				pageIndex={search.getPageIndex()}
				setPageIndex={handleChangePageIndex}
				listItemAmount={search.getPageSize()}
				maxNumberOfButtons={maxNumberOfButtons}
			/>
		</main>
	);
};

export default RecipeSearchComponent;
